/*
  RequireAdmin — server-validated Owner/Admin route gate.

  Fail-closed:
    * while AuthContext is still resolving (loading), render nothing —
      no protected content is ever mounted before /api/auth/me returns;
    * if not signed in (user === false), redirect to /login;
    * if signed in but not the Owner/Admin, render the deny page and never
      mount children;
    * only when {user, isAdmin} both resolve truthy do the children render.

  Deliberately does NOT read from localStorage. The server response from
  /api/auth/me is the sole source of truth. Seeded/modified localStorage
  entries cannot unlock protected content.
*/
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";

export function RequireAdmin({ children }) {
  const { user, isAdmin, loading, logout } = useAuth();

  if (loading) {
    return (
      <PageShell>
        <div className="container py-14 text-sm text-muted-foreground" data-testid="require-admin-loading">
          Verifying access&hellip;
        </div>
      </PageShell>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (!isAdmin) {
    return (
      <PageShell>
        <div className="container py-14" data-testid="require-admin-denied">
          <p className="eyebrow">Restricted</p>
          <h1 className="mt-3 font-display text-4xl font-medium text-foreground">Owner access only</h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            You are signed in as {user.email}, but this section is limited to the Spiral
            Beyond Compliance owner account for internal consultancy delivery. Please sign
            out and try again with the authorised account, or return to the overview.
          </p>
          <div className="mt-6 flex gap-2">
            <Button asChild variant="outline" className="rounded-sm">
              <Link to="/"><ArrowLeft className="h-4 w-4" /> Back to overview</Link>
            </Button>
            <Button onClick={logout} className="rounded-sm" data-testid="require-admin-signout-btn">
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }

  return children;
}

export default RequireAdmin;
